import is from '@sindresorhus/is';
import {
    createContext,
    type FunctionComponent,
    type PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useRef,
} from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from '~/ui/core/context-menu/ContextMenu';
import { IconChatBubble7, IconCopy } from '~/ui/core/icons';

import { TITLE_ANCHOR_SELECTOR } from './anchor/grammar';
import { useCommentableAnchor } from './anchor/use-commentable-anchor';
import { ContainedSelectionCommentButton } from './ContainedSelectionCommentButton';
import { type CommentHighlightState, commentHighlightClassName } from './comment-highlight';
import { TitleCommentRowContext, type TitleCommentRowContextValue } from './title-comment-row-context';
import { useCopyTextToClipboard } from './use-copy-text-to-clipboard';

type TitleThreadState = {
    readonly hasComment: boolean;
    readonly highlightClassName: string;
    readonly isActive: boolean;
    readonly state: CommentHighlightState;
};

const resolveTitleHighlightClassName = (state: CommentHighlightState): string =>
    state === 'none' ? '' : commentHighlightClassName(state);

const readRowTextSelection = (row: HTMLElement | null): string | null => {
    if (is.null(row)) return null;
    const selection = window.getSelection();
    if (is.null(selection) || selection.isCollapsed || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!row.contains(range.startContainer) || !row.contains(range.endContainer)) return null;
    const text = range.toString();
    return is.emptyStringOrWhitespace(text) ? null : text;
};

const TitleThreadActiveContext = createContext<TitleThreadState>({
    hasComment: false,
    highlightClassName: '',
    isActive: false,
    state: 'none',
});

export const useIsTitleThreadActive = (): boolean => useContext(TitleThreadActiveContext).isActive;

export const useTitleThreadState = (): TitleThreadState => useContext(TitleThreadActiveContext);

type CommentableTitleProps = PropsWithChildren<{
    readonly itemId: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly selectionCommentEnabled?: boolean;
    readonly title: string;
}>;

export const CommentableTitle: FunctionComponent<CommentableTitleProps> = ({
    children,
    itemId,
    newThreadCreationDisabled = false,
    selectionCommentEnabled = false,
    title,
}) => {
    const translate = useTranslate();
    const rowRef = useRef<HTMLDivElement | null>(null);
    const titleInputRef = useRef<HTMLSpanElement | null>(null);
    const copyTextToClipboard = useCopyTextToClipboard();
    const { attrs, state, commentId, openThread, startComment } = useCommentableAnchor({ kind: 'title', itemId });

    const handleComment = useCallback((): void => {
        startComment(rowRef.current?.querySelector<HTMLElement>(TITLE_ANCHOR_SELECTOR) ?? null);
    }, [startComment]);

    const handleSelectionComment = useCallback(
        (anchorRect: DOMRect): void => {
            startComment(rowRef.current?.querySelector<HTMLElement>(TITLE_ANCHOR_SELECTOR) ?? null, { anchorRect });
        },
        [startComment],
    );

    const handleCopy = useCallback((): void => {
        const selectedText = readRowTextSelection(rowRef.current);
        if (!is.null(selectedText)) {
            void copyTextToClipboard(selectedText);
            return;
        }
        void copyTextToClipboard(titleInputRef.current?.textContent ?? title);
    }, [copyTextToClipboard, title]);

    const rowValue = useMemo<TitleCommentRowContextValue>(
        () => ({
            rowDataAttrs: { ...attrs, 'data-testid': 'commentable-title' },
            onRowClick: is.undefined(commentId) ? undefined : openThread,
            titleInputRef,
        }),
        [attrs, commentId, openThread],
    );

    const providedThreadState = useMemo<TitleThreadState>(
        () => ({
            hasComment: state === 'saved' || state === 'active',
            highlightClassName: resolveTitleHighlightClassName(state),
            isActive: state === 'active',
            state,
        }),
        [state],
    );

    return (
        <TitleCommentRowContext.Provider value={rowValue}>
            <TitleThreadActiveContext.Provider value={providedThreadState}>
                <ContextMenu>
                    <ContextMenuTrigger>
                        <div ref={rowRef} className="contents">
                            {children}
                        </div>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="!min-w-[7rem]" onCloseAutoFocus={(event) => event.preventDefault()}>
                        {!newThreadCreationDisabled && (
                            <ContextMenuItem onSelect={handleComment}>
                                <IconChatBubble7 size={12} />
                                {translate('pages.job-architecture-v2.comments.selection-button.label')}
                            </ContextMenuItem>
                        )}
                        <ContextMenuItem onSelect={handleCopy}>
                            <IconCopy size={12} />
                            {translate('common.copy')}
                        </ContextMenuItem>
                    </ContextMenuContent>
                </ContextMenu>
                {selectionCommentEnabled ? (
                    <ContainedSelectionCommentButton
                        containerRef={rowRef}
                        disabled={newThreadCreationDisabled}
                        onComment={handleSelectionComment}
                    />
                ) : null}
            </TitleThreadActiveContext.Provider>
        </TitleCommentRowContext.Provider>
    );
};
